import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../auth/AuthProvider';
import useSettingsStore from '../../store/useSettingsStore';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { ScrollArea } from '../ui/scroll-area';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent
} from '../ui/dropdown-menu';
import {
  Settings, LogOut,
  ChevronRight, ChevronDown, User
} from 'lucide-react';

import { menuGroups } from '../../pages/data/menuConfig';
import { Icon } from '../ui/icon';

const SubGroup = ({ item, location }) => {
  const { t } = useTranslation();
  const hasActiveChild = (menuItem) => {
    if (!menuItem.sub) return false;
    return menuItem.sub.some(s => {
      if (location.pathname === s.path) return true;
      if (s.sub) return hasActiveChild(s);
      return false;
    });
  };

  const isSubActive = hasActiveChild(item);
  const [isOpen, setIsOpen] = useState(isSubActive);
  const isActive = location.pathname === item.path || isSubActive;

  React.useEffect(() => {
    if (isSubActive) {
      setIsOpen(true);
    }
  }, [location.pathname, isSubActive]);

  if (item.sub) {
    return (
      <div className="space-y-1">
        <button
          onClick={() => setIsOpen(!isOpen)}
          id={location.pathname === item.path ? 'active-sidebar-item' : undefined}
          className={cn(
            "w-full flex items-center justify-between text-sm py-2 px-3 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted/80 relative",
            isActive && "text-primary bg-primary/5 font-semibold border-s-4 border-primary rounded-s-none"
          )}
        >
          <span className={cn(
            "absolute start-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full transition-all",
            isActive ? "bg-primary" : "bg-transparent border border-muted-foreground/30"
          )} />
          <span className="ms-3 flex-1 text-left truncate">{t(item.label)}</span>
          <Icon icon={ChevronRight} size="xs" className={cn("transition-transform text-muted-foreground/70 ms-auto", isOpen && "rotate-90")} />
        </button>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden space-y-1 pt-1 pb-0.5 ps-4 border-s border-border/60 ms-3.5"
            >
              {item.sub.map((subItem, index) => (
                <SubGroup key={subItem.path || index} item={subItem} location={location} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <NavLink
      to={item.path}
      id={location.pathname === item.path ? 'active-sidebar-item' : undefined}
      className={({ isActive: childActive }) => cn(
        "block text-sm py-2 px-3 rounded-md transition-colors relative",
        childActive
          ? "text-primary font-semibold bg-primary/5"
          : "text-muted-foreground hover:text-foreground hover:bg-muted/80"
      )}
    >
      {({ isActive: childActive }) => (
        <>
          <span className={cn(
            "absolute start-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full transition-all",
            childActive ? "bg-primary" : "bg-transparent border border-muted-foreground/30"
          )} />
          <span className="ms-3 flex-1 text-left truncate">{t(item.label)}</span>
        </>
      )}
    </NavLink>
  );
};

const renderCollapsedSubItems = (s, location, t) => {
  if (s.sub) {
    const hasActiveChild = (menuItem) => {
      if (!menuItem.sub) return false;
      return menuItem.sub.some(child => {
        if (location.pathname === child.path) return true;
        if (child.sub) return hasActiveChild(child);
        return false;
      });
    };

    const isSubActive = hasActiveChild(s);

    return (
      <DropdownMenuSub key={s.path || s.label}>
        <DropdownMenuSubTrigger className={cn("cursor-pointer", isSubActive && "text-primary font-semibold focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground")}>
          <span className="text-sm">{t(s.label)}</span>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="w-48 p-1">
          {s.sub.map(child => renderCollapsedSubItems(child, location, t))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  const childActive = location.pathname === s.path;
  return (
    <DropdownMenuItem key={s.path} asChild>
      <NavLink
        to={s.path}
        id={childActive ? 'active-sidebar-item' : undefined}
        className={cn(
          "w-full flex items-center px-3 py-2 rounded-md text-sm transition-colors cursor-pointer my-0.5",
          childActive
            ? "bg-primary text-primary-foreground font-semibold"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )}
      >
        {t(s.label)}
      </NavLink>
    </DropdownMenuItem>
  );
};

const NavItem = ({ item, isCollapsed, isIconOnly }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const hasActiveChild = (menuItem) => {
    if (!menuItem.sub) return false;
    return menuItem.sub.some(s => {
      if (location.pathname === s.path) return true;
      if (s.sub) return hasActiveChild(s);
      return false;
    });
  };

  const isSubActive = hasActiveChild(item);
  const isActive = location.pathname === item.path || isSubActive;
  const isFull = !isCollapsed && !isIconOnly;

  React.useEffect(() => {
    if (item.sub) {
      setOpen(!!isSubActive);
    }
  }, [location.pathname, isSubActive, item.sub]);

  const renderContent = () => (
    <>
      <item.icon className={cn(
        "shrink-0 transition-all w-5 h-5",
        isActive || open ? "text-primary" : "text-muted-foreground"
      )} />
      <AnimatePresence>
        {isFull && (
          <motion.span
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            className="truncate flex-1 text-start leading-none ms-3"
          >
            {t(item.label)}
          </motion.span>
        )}
      </AnimatePresence>
      {isFull && item.sub && (
        <ChevronRight className={cn("w-4 h-4 transition-transform text-muted-foreground rtl:-scale-x-100", open && "rotate-90")} />
      )}
    </>
  );

  const wrapperClass = cn(
    "w-full flex items-center px-3 py-2.5 rounded-md text-sm transition-colors relative group",
    (isCollapsed || isIconOnly) ? "justify-center" : "",
    isActive || open
      ? "text-primary bg-primary/5 font-semibold border-s-4 border-primary rounded-s-none"
      : "font-medium text-muted-foreground hover:text-foreground hover:bg-muted"
  );

  if (item.sub) {
    if (!isFull) {
      return (
        <DropdownMenu>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <button className={wrapperClass}>
                    {renderContent()}
                  </button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="right" className="font-semibold">{t(item.label)}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <DropdownMenuContent side="right" align="start" className="w-56 p-1 max-h-[80vh] overflow-y-auto" sideOffset={8}>
            <DropdownMenuLabel className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t(item.label)}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {item.sub.map(s => renderCollapsedSubItems(s, location, t))}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    return (
      <div>
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={() => setOpen(!open)} className={wrapperClass}>
                {renderContent()}
              </button>
            </TooltipTrigger>
            {(isCollapsed || isIconOnly) && (
              <TooltipContent side="right" className="font-semibold">{t(item.label)}</TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="pt-1 pb-1 ps-9 space-y-1">
                {item.sub.map((s, index) => (
                  <SubGroup key={s.path || index} item={s} location={location} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <NavLink to={item.path} id={location.pathname === item.path ? 'active-sidebar-item' : undefined} className={wrapperClass}>
            {renderContent()}
          </NavLink>
        </TooltipTrigger>
        {(isCollapsed || isIconOnly) && (
          <TooltipContent side="right" className="font-semibold">{t(item.label)}</TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};

const SectionLabel = ({ children, isVisible }) => (
  <AnimatePresence>
    {isVisible && (
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="px-3 mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground/70 select-none"
      >
        {children}
      </motion.p>
    )}
  </AnimatePresence>
);

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const {
    sidebarMode,
    isSidebarOpen,
    showSidebarProfile, primaryColor,
    layoutContainer, rtl
  } = useSettingsStore();

  const isCollapsed = sidebarMode === 'collapsed';
  const isIconOnly = sidebarMode === 'icon-only';
  const isFull = !isCollapsed && !isIconOnly;

  React.useEffect(() => {
    const timer = setTimeout(() => {
      const activeItem = document.getElementById('active-sidebar-item');
      if (activeItem) {
        activeItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname, isSidebarOpen, sidebarMode]);

  if (!isSidebarOpen) return null;

  const sidebarWidth = isIconOnly ? 72 : isCollapsed ? 88 : 260;
  const isBoxed = layoutContainer === 'boxed';

  return (
    <motion.aside
      initial={false}
      animate={{ width: sidebarWidth }}
      transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
      className="fixed start-0 top-0 h-screen z-40 flex flex-col overflow-hidden bg-card border-e border-border shadow-sm shrink-0"
      style={{
        left: isBoxed ? (!rtl ? 'max(0px, calc((100vw - 1440px) / 2))' : 'auto') : undefined,
        right: isBoxed ? (rtl ? 'max(0px, calc((100vw - 1440px) / 2))' : 'auto') : undefined,
      }}
    >
      {/* Brand Logo */}
      <div className="h-16 flex items-center px-6 shrink-0 border-b border-border gap-3">
        <a href="/" className={cn("flex items-center gap-2", (isCollapsed || isIconOnly) ? "mx-auto px-0" : "")}>
          <div className="w-8 h-8 rounded flex items-center justify-center shrink-0" style={{ backgroundColor: primaryColor }}>
            <span className="text-white font-black text-lg select-none leading-none">S</span>
          </div>
          <AnimatePresence>
            {isFull && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                className="font-bold text-xl text-foreground tracking-tight whitespace-nowrap"
              >
                ShadeX
              </motion.span>
            )}
          </AnimatePresence>
        </a>
      </div>

      {/* Navigation Links */}
      <ScrollArea className="flex-1 py-6 px-3">
        <nav className="space-y-6">
          {menuGroups.map((group, idx) => (
            <div key={idx}>
              <SectionLabel isVisible={isFull}>{t(group.label)}</SectionLabel>
              <div className="space-y-1">
                {group.items.map((item, i) => (
                  <NavItem key={i} item={item} isCollapsed={isCollapsed} isIconOnly={isIconOnly} />
                ))}
              </div>
            </div>
          ))}
        </nav>
      </ScrollArea>

      {/* Footer / User Profile */}
      {showSidebarProfile && (
        <div className="shrink-0 p-3 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className={cn(
                "flex items-center gap-3 p-2.5 rounded-xl transition-all cursor-pointer group hover:bg-muted w-full select-none outline-none focus:bg-muted",
                (isCollapsed || isIconOnly) && "justify-center"
              )}>
                <div className="relative shrink-0">
                  <Avatar className="h-8 w-8 ring-2 ring-border">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback className="text-white text-sm font-bold" style={{ backgroundColor: primaryColor }}>
                      {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="absolute bottom-0 end-0 w-2 h-2 bg-success rounded-full ring-2 ring-background" />
                </div>

                <AnimatePresence>
                  {isFull && (
                    <motion.div
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      className="flex-1 min-w-0 overflow-hidden text-start"
                    >
                      <p className="text-sm font-semibold text-foreground truncate leading-none mb-1">
                        {user?.name || 'Admin User'}
                      </p>
                      <p className="text-[10px] text-muted-foreground leading-none">
                        Administrator
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>

                {isFull && (
                  <Icon icon={ChevronDown} size="xs" className="ms-auto transition-transform group-hover:text-foreground" />
                )}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" side="right" sideOffset={12}>
              <div className="px-2 py-1.5 mb-1">
                <p className="text-sm font-semibold text-foreground">{user?.name || 'Admin User'}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email || 'admin@example.com'}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-danger focus:text-danger focus:bg-danger/10 cursor-pointer"
                onClick={logout}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </motion.aside>
  );
};

export default Sidebar;